import { Component, EventEmitter, Output, inject } from '@angular/core';
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { AuthService } from '@nimmsta/ng-commons-lib/auth';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import {
  InputWithErrorComponent,
  SaveableFormComponent,
} from '@nimmsta/ng-commons-lib/forms';
import {
  Software,
  SoftwarePlatform,
  SoftwareStatus,
  SoftwareType,
  SoftwareVisibility,
  UserRole,
} from '@nimmsta/ng-commons-lib/models';
import { SoftwareService } from '@nimmsta/ng-commons-lib/services/software';
import { SoftwareTypeService } from '../services/software/software-type.service';
import { SoftwarePlatformService } from '../services/software/software-platform.service';

@Component({
  selector: 'app-software-form',
  templateUrl: 'software-form.component.html',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    InputWithErrorComponent,
    ErrorComponent,
  ],
})
export class SoftwareFormComponent extends SaveableFormComponent<Software> {
  private fb = inject(FormBuilder);
  private softwareTypeService = inject(SoftwareTypeService);
  private softwarePlatformService = inject(SoftwarePlatformService);

  protected override dataService = inject(SoftwareService);

  @Output() override closeEvent = new EventEmitter();

  @Output() override loadingEvent = new EventEmitter<boolean>();

  softwareStatusItems = Object.values(SoftwareStatus);

  softwareStatusNames?: string[];

  softwareVisibilityItems = Object.values(SoftwareVisibility);

  softwareVisibilityNames?: string[];

  softwareTypes: SoftwareType[] = [];

  softwareTypeIds: string[] = [];

  softwareTypeNames: string[] = [];

  softwarePlatforms: SoftwarePlatform[] = [];

  softwarePlatformIds: string[] = [];

  softwarePlatformNames: string[] = [];

  constructor() {
    const translate = inject(TranslateService);
    const authService = inject(AuthService);

    super();
    this.setValue({} as Software);
    this.softwareStatusNames = this.softwareStatusItems.map((item) =>
      translate.instant(`software.statusItems.${item.toLowerCase()}`),
    );
    this.softwareVisibilityNames = this.softwareVisibilityItems.map((item) =>
      translate.instant(`software.visibilityItems.${item.toLowerCase()}`),
    );

    // Only load software types and platforms if the user is an admin
    authService.getCurrentUser().subscribe((user) => {
      if (user.role === UserRole.ADMIN) {
        this.softwareTypeService.list().subscribe((page) => {
          this.softwareTypes = page.content;
          this.softwareTypeIds = this.softwareTypes.map((item) => item.id!);
          this.softwareTypeNames = this.softwareTypes.map((item) => item.name!);
        });
        this.softwarePlatformService.list().subscribe((page) => {
          this.softwarePlatforms = page.content;
          this.softwarePlatformIds = this.softwarePlatforms.map(
            (item) => item.id!,
          );
          this.softwarePlatformNames = this.softwarePlatforms.map(
            (item) => item.name!,
          );
        });
      }
    });
  }

  override onSave() {
    const selectedType = this.form.get('type')?.value;
    const selectedPlatform = this.form.get('platform')?.value;
    if (selectedType) {
      this.value.type =
        this.softwareTypes.find((item) => item.id === selectedType) ||
        undefined;
    }
    if (selectedPlatform) {
      this.value.platform =
        this.softwarePlatforms.find((item) => item.id === selectedPlatform) ||
        undefined;
    }
    this.form.patchValue({
      ...this.form.value,
      type: this.value.type,
      platform: this.value.platform,
    });
    super.onSave();
  }

  protected setFormGroup(): void {
    this.form = this.fb.group({
      id: this.value.id,
      name: [this.value.name, [Validators.required]],
      description: [this.value.description],
      status: [this.value.status, [Validators.required]],
      visibility: [this.value.visibility, [Validators.required]],
      type: [this.value.type?.id],
      platform: [this.value.platform?.id],
      iconUrl: [this.value.iconUrl],
    });
  }
}
